<template>
  <block
    component-name="block-carousel-slides"
    ref="tpl_root"
    :px="false"
    body-class="overflow-hidden flex max-h-small-screen h-small-screen contain-strict"
    body-skin="black"
  >
    <template v-slot:default="blockData">
      <!-- SLIDER -->
      <swiper-container
        init="false"
        class="w-full h-full text-skin-base bg-skin-base"
        events-prefix="swiper-"
      >
        <swiper-slide
          v-for="(item, index) in $attrs.data.body.items"
          class="relative top-0 left-0"
        >
          <div class="absolute inset-0 grid w-full h-full grid-cols-1">
            <media
              v-bind="item.asset"
              :sizes="{
                base: 600,
                phablet: 1020,
                tablet: 1272,
                laptop: 1512,
                desktop: 1920,
                extdesktop: 2560,
              }"
              :loading="
                index > 1 || blockData?.loading === 'lazy' ? 'lazy' : 'eager'
              "
              class="slide-media block w-full h-full row-start-1 row-end-2 origin-center col-span-full"
            />

            <span
              class="relative top-0 left-0 w-full h-full row-start-1 row-end-2 z-2 col-span-full bg-black/40 phablet:bg-transparent phablet:bg-gradient-to-r phablet:from-black/50 phablet:to-transparent"
            ></span>
          </div>

          <section
            class="overflow-hidden relative z-3 top-0 left-0 text-skin-base flex flex-col gap-lg justify-center items-start p-edge h-full w-full phablet:max-w-[50vw] desktop:max-w-[33vw]"
          >
            <div
              v-if="item.overline"
              class="w-full select-none text-overline-big"
              v-html="item.overline"
              data-swiper-parallax-x="-50%"
              data-swiper-parallax-opacity="0"
            ></div>

            <div
              v-if="item.title"
              class="w-full select-none text-heading-4"
              v-html="item.title"
              data-swiper-parallax-x="-60%"
              data-swiper-parallax-opacity="0"
            ></div>

            <div
              v-if="item.text"
              class="w-full select-none text-body-big line-clamp-[7] tablet:line-clamp-none"
              v-html="item.text"
              data-swiper-parallax-x="-70%"
              data-swiper-parallax-opacity="0"
            ></div>

            <div
              class="w-full select-none"
              data-swiper-parallax-x="-80%"
              data-swiper-parallax-opacity="0"
            >
              <btn-nav
                v-if="item.button?.action"
                :title="$t.labels.read_more"
                :action="item.button.action"
              />
            </div>
          </section>
        </swiper-slide>
      </swiper-container>
      <!-- end SLIDER -->

      <div
        v-if="$attrs.data.body.items.length > 1"
        class="absolute bottom-0 left-0 w-full transition-transform duration-500 ease-in-out z-2 text-skin-base"
        :class="{ 'translate-y-full !pointer-events-none': UIhidden }"
      >
        <!-- PAGER -->
        <swiper-container
          init="false"
          class="w-full h-20 block-carousel-slides_swiper-container-menu laptop:h-30 bg-black/20"
          events-prefix="swiper-"
        >
          <swiper-slide
            v-for="(item, index) in $attrs.data.body.items"
            class="relative top-0 left-0 flex items-center px-2 laptop:px-5 max-w-min pointer-events-none"
          >
            <div class="pointer-events-auto min-w-min">
              <btn-nav
                :title="item.overline ? item.overline : item.asset.alt"
                icon-left="bullet"
                icon-right="bullet"
                class="[&_svg]:transition-opacity [&_svg]:duration-300 [&_svg]:ease-in-out [&>*:last-child]:opacity-0"
                :class="{
                  '[&>*:first-child]:opacity-100': currentIndex === index,
                  '[&>*:first-child]:opacity-0': currentIndex !== index,
                }"
                :pressed="currentIndex === index"
                @click="() => swiperCarouselEl.swiper.slideTo(index)"
              />
            </div>
          </swiper-slide>
        </swiper-container>
        <!-- end PAGER -->
      </div>
    </template>
  </block>
</template>

<script setup>
const tpl_root = templateRef("tpl_root");
const currentIndex = ref(0);
const UIhidden = ref(false);
const tpl_body = templateRef("tpl_body");
const attrs = useAttrs();

let UIsc;
let swiperCarouselEl, swiperMenuEl;

useSafeMountedEl(tpl_root, () => {
  swiperCarouselEl = tpl_root.value.$el.querySelectorAll("swiper-container")[0];
  swiperMenuEl = tpl_root.value.$el.querySelectorAll("swiper-container")[1];

  Object.assign(swiperCarouselEl, {
    preventInteractionOnTransition: true,
    grabCursor: attrs.data.body.items.length > 1,
    parallax: true,
    speed: 650,
    followFinger: false,
    allowTouchMove: attrs.data.body.items.length > 1,
    slidesPerView: 1,
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
      slideChange(swiper) {
        currentIndex.value = swiper.realIndex;

        if (currentIndex.value && swiperMenuEl?.swiper)
          swiperMenuEl.swiper.slideTo(currentIndex.value);
      },
    },
  });

  if (attrs.data.body.items.length > 1) {
    Object.assign(swiperMenuEl, {
      slidesPerView: "auto",
      centerInsufficientSlides: true,
      slideToClickedSlide: true,
    });
  }

  swiperCarouselEl.initialize();

  if (attrs.data.body.items.length > 1) {
    swiperMenuEl.initialize();

    UIsc = ScrollTrigger.create({
      trigger: tpl_root.value.$el.querySelector(".component-body"),
      start: () => `top -300px`,
      end: () => `bottom 0`,
      onEnter: () => {
        UIhidden.value = true;
      },
      onEnterBack: () => {
        UIhidden.value = true;
      },
      onLeave: () => {
        UIhidden.value = false;
      },
      onLeaveBack: () => {
        UIhidden.value = false;
      },
    });
  }
});

onBeforeUnmount(() => {
  if (swiperCarouselEl?.swiper) {
    swiperCarouselEl?.swiper?.destroy();
    swiperCarouselEl = null;
  }

  if (swiperMenuEl?.swiper) {
    swiperMenuEl?.swiper?.destroy();
    swiperMenuEl = null;
  }

  if (UIsc) {
    UIsc.kill();
    UIsc = null;
  }
});
</script>

<style>
[component-name="block-carousel-slides"] swiper-slide {
  & .slide-media * {
    opacity: 0.6;
    transform: scale(1.1);
    transition-property: transform, opacity, color;
    transition-timing-function: cubic-bezier(0.075, 0.82, 0.165, 1);
    transition-duration: 1000ms;
    transition-delay: 600ms;
  }

  &.swiper-slide-active .slide-media * {
    opacity: 1;
    transform: scale(1);
    transition-duration: 5200ms;
    transition-delay: 0ms;
  }
}
</style>
